import { create } from "zustand";

import { getErrorMessage } from "@/lib/api/errors";

export type NotificationSeverity = "error" | "warning" | "info" | "success";

export type AppNotification = {
  id: number;
  message: string;
  severity: NotificationSeverity;
};

interface NotificationState {
  queue: AppNotification[];
  notify: (message: string, severity?: NotificationSeverity) => void;
  dismiss: (id: number) => void;
}

const MAX_QUEUE = 3;
let nextId = 1;

export const useNotificationStore = create<NotificationState>()((set) => ({
  queue: [],

  // A message already waiting is not queued twice, so a burst of failing
  // requests (e.g. the server going down) shows a single toast.
  notify: (message, severity = "info") =>
    set((state) => {
      if (state.queue.some((item) => item.message === message)) return state;
      const queue = [...state.queue, { id: nextId++, message, severity }];
      return { queue: queue.slice(-MAX_QUEUE) };
    }),

  dismiss: (id) => set((state) => ({ queue: state.queue.filter((item) => item.id !== id) })),
}));

export const notify = (message: string, severity?: NotificationSeverity) =>
  useNotificationStore.getState().notify(message, severity);

export const notifyError = (error: unknown, fallback?: string) =>
  notify(getErrorMessage(error, fallback), "error");
